import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import repo from '@/lib/repository';
import {
  CalendarEntryDto,
  HealthReportDto,
  PatientDto,
  ProgramDto,
  SpecialistTodoDto,
  UpdatePatientDayProgram,
} from '@/lib/types';
import { validateServerError } from '@/lib/utils';
import type { UserActivityFeedbackDto } from '@/store/types';

export const getPatients = createAsyncThunk(
  'patients/get/list',
  async (_, { rejectWithValue }) => {
    try {
      const response = await repo.get<PatientDto[]>(
        '/specialists/patients/list',
        { headers: { 'content-type': 'application/json' } },
      );
      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(validateServerError(error));
    }
  },
);

// Raw shape returned by backend (supports snake_case or camelCase)
type RawFeedbackRow = {
  id: string;
  user?: { id: string };
  exerciseName?: string;
  exercise_name?: string;
  exerciseImageUrl?: string;
  exercise_image_url?: string;
  groupName?: string;
  group_name?: string;
  groupDisplayName?: string;
  group_display_name?: string;
  subjectType?: string;
  subject_type?: string;
  subjectId?: string;
  subject_id?: string;
  exerciseType?: string;
  exercise_type?: string;
  category?: string;
  exerciseId?: number | null;
  exercise_id?: number | null;
  groupId?: string;
  group_id?: string;
  groupIndex?: string;
  group_index?: string;
  feedback?: string;
  feedbackNote?: string;
  feedback_note?: string;
  created: string | number | Date;
};

export const getPatientFeedbacks = createAsyncThunk(
  'patients/get/feedbacks',
  async (data: { userId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const url = `/patients/list/${data.userId}/feedback/list${
        data.limit ? `?limit=${data.limit}` : ''
      }`;
      const response = await repo.get<RawFeedbackRow[]>(url, {
        headers: { 'content-type': 'application/json' },
      });
      const mapped: UserActivityFeedbackDto[] = (response.data || []).map(
        (r) => ({
          id: r.id,
          user: r.user,
          exerciseName: r.exerciseName ?? r.exercise_name,
          exerciseImageUrl: r.exerciseImageUrl ?? r.exercise_image_url,
          groupName: r.groupName ?? r.group_name,
          groupDisplayName: r.groupDisplayName ?? r.group_display_name,
          subjectType: r.subjectType ?? r.subject_type,
          subjectId: r.subjectId ?? r.subject_id,
          exerciseType: r.exerciseType ?? r.exercise_type,
          category: r.category,
          exerciseId: r.exerciseId ?? r.exercise_id ?? null,
          groupId: r.groupId ?? r.group_id,
          groupIndex: r.groupIndex ?? r.group_index,
          feedback: r.feedback,
          feedbackNote: r.feedbackNote ?? r.feedback_note,
          created: r.created,
        }),
      );
      return mapped;
    } catch (error) {
      return rejectWithValue(validateServerError(error));
    }
  },
);

export const getPatientsTodo = createAsyncThunk(
  'patients/get/todo',
  async (data: { type: string; date: number }, { rejectWithValue }) => {
    try {
      const response = await repo.get<SpecialistTodoDto[]>(
        `/specialists/patients/todo?type=${data.type}&date=${data.date}`,
        { headers: { 'content-type': 'application/json' } },
      );
      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(validateServerError(error));
    }
  },
);

export const getPatientHealthReport = createAsyncThunk<
  HealthReportDto[],
  string
>('patients/get/health-report', async (userId, { rejectWithValue }) => {
  try {
    const response = await repo.get<HealthReportDto[]>(
      `/patients/list/${userId}/health-report`,
      { headers: { 'content-type': 'application/json' } },
    );
    return response.data;
  } catch (error: any | AxiosError) {
    return rejectWithValue(validateServerError(error));
  }
});

export const getPatient = createAsyncThunk(
  'patients/get/one',
  async (data: { userId: string | undefined }, { rejectWithValue }) => {
    try {
      const response = await repo.get<PatientDto>(
        `/patients/list/${data.userId}`,
        { headers: { 'content-type': 'application/json' } },
      );
      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(validateServerError(error));
    }
  },
);

export const getPatientProgram = createAsyncThunk(
  'patients/get/program/one',
  async (
    data: { userId: string | undefined; day: number; type: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await repo.get<ProgramDto>(
        `/patients/list/${data.userId}/program?day=${data.day}&type=${data.type}`,
        { headers: { 'content-type': 'application/json' } },
      );
      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(validateServerError(error));
    }
  },
);

export const updatePatientDayProgram = createAsyncThunk(
  'patients/update/day/program',
  async (
    {
      userId,
      day,
      finish,
      level,
      programId,
      start,
      type,
    }: { userId: string } & UpdatePatientDayProgram,
    { rejectWithValue },
  ) => {
    try {
      const response = await repo.put<CalendarEntryDto[]>(
        `/patients/list/${userId}/program`,
        { day, finish, level, programId, start, type },
        { headers: { 'content-type': 'application/json' } },
      );
      return response.data;
    } catch (error: any | AxiosError) {
      return rejectWithValue(validateServerError(error));
    }
  },
);
